import React, { useRef, memo } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Dimensions,
} from "react-native";
import Caret from "@assets/svg/Caret";
import Text from "@components/AppText";
import colors from "@constants/color";
import metrics from "@constants/metrics";
import CustomText from "@components/CustomText";
import UpArrow from "@assets/svg/Wallet/UpArrow";
import getStatusColor, {
  getWithDrawalAmountColor,
} from "../../utils/getStatusColor";

import t from "@constants/localization";

const PATH = "screens/Wallet/WithdrawalCard";

const WithDrawalCard = memo((props) => {
  const isExpanded = useRef(false);
  const animatedHeight = useRef(new Animated.Value(0));
  const animatedCaretRotation = useRef(new Animated.Value(0));

  const {
    title,
    value,
    status,
    date,
    statusKey,
    shouldShowPassbookType = false,
    typeHi = "",
    withdrawal_mode = "",
    upi_address = "",
    account_number = "",
    is_blacklisted = false,
    failure_reason = "",
    utr = "",
    navigation = {},
    accounts,
    isPassbookScreen = true,
    containerStyle = {},
  } = props;

  const toggleAnimation = () => {
    Animated.parallel([
      Animated.timing(animatedHeight.current, {
        toValue: isExpanded.current ? 0 : failure_reason ? 160 : 100,
        duration: 200,
      }),
      Animated.timing(animatedCaretRotation.current, {
        toValue: isExpanded.current ? 0 : 1,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();

    isExpanded.current = !isExpanded.current;
  };

  const currentAccount = () => {
    let account_type = "vpa";

    if (account_number) {
      account_type = "bank_account";
    }

    return {
      type: account_type,
      account_data: accounts.find((account) =>
        account_type === "vpa"
          ? account.upi_address === upi_address
          : account.account_number === account_number,
      ),
    };
  };

  return (
    <Animated.View
      style={[
        {
          overflow: "hidden",
          marginTop: 5,
          width: metrics.screenWidth - 30,
        },
        styles.cardContainer,
        containerStyle,
      ]}
    >
      <View
        style={[
          styles.beLikeRow,
          { paddingHorizontal: 15, paddingTop: 12, marginBottom: 10 },
        ]}
      >
        {shouldShowPassbookType && (
          <CustomText
            style={{ color: colors.lightBlack, flex: 1 }}
            variant="caption_1"
          >
            {typeHi}
          </CustomText>
        )}
        <UpArrow fill={getStatusColor(statusKey)} style={{ marginRight: 5 }} />
        <CustomText style={styles.primaryText} variant="body_text_3">
          {date}
        </CustomText>
      </View>
      <View
        style={[
          styles.beLikeRow,
          {
            justifyContent: "space-between",
            paddingHorizontal: 15,
            paddingBottom: 12,
          },
        ]}
      >
        <View style={{ width: "60%", alignSelf: "flex-start" }}>
          <Text>{title}</Text>
        </View>
        <View style={{ alignItems: "flex-end" }}>
          {/*! shouldShowPassbookType && (
            <CustomText style={styles.primaryText} variant="caption_2">
              {date}
            </CustomText>
          ) */}
          <TouchableOpacity
            style={[
              styles.beLikeRow,
              { marginVertical: 0, alignSelf: "flex-start", marginTop: 6 },
            ]}
            onPress={toggleAnimation}
          >
            <CustomText
              style={[{ marginHorizontal: 2 }, { color: "black" }]}
              variant="subtitle_16"
            >
              -{value}
            </CustomText>

            <Animated.View
              style={{
                transform: [
                  {
                    rotate: animatedCaretRotation.current.interpolate({
                      inputRange: [0, 1],
                      outputRange: ["90deg", "270deg"],
                    }),
                  },
                ],
              }}
            >
              <Caret strokeColor={colors.primary} width={18} height={15} />
            </Animated.View>
          </TouchableOpacity>
          <CustomText
            style={{ color: getStatusColor(statusKey), marginTop: 6 }}
            variant="caption_1"
          >
            {status}
          </CustomText>
        </View>
      </View>
      <Animated.View
        style={{
          overflow: "hidden",
          height: animatedHeight.current,
        }}
      >
        <View
          style={{
            height: failure_reason ? 160 : 100,
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            padding: 20,
          }}
        >
          <View style={[styles.beLikeRow, { justifyContent: "space-between" }]}>
            <CustomText
              style={{
                color: "rgba(0, 0, 0, 0.56)",
                fontFamily: "NotoSansDevanagari-Regular",
                fontSize: 12,
              }}
            >
              {t(`${PATH}/transactionId`)}
            </CustomText>
            <CustomText
              style={{
                color: "rgba(0, 0, 0, 0.56)",
                fontFamily: "NotoSansDevanagari-Bold",
                fontSize: 12,
              }}
            >
              {utr}
            </CustomText>
          </View>
          {!failure_reason ? (
            <>
              <View
                style={[
                  styles.beLikeRow,
                  {
                    justifyContent: "space-between",
                    paddingVertical: 5,
                  },
                ]}
              >
                <CustomText
                  style={{
                    color: "rgba(0, 0, 0, 0.56)",
                    fontFamily: "NotoSansDevanagari-Regular",
                    fontSize: 12,
                  }}
                >
                  {t(`${PATH}/withdrawalMode`)}
                </CustomText>
                <CustomText
                  style={{
                    color: "rgba(0, 0, 0, 0.56)",
                    fontFamily: "NotoSansDevanagari-Bold",
                    fontSize: 12,
                  }}
                >
                  {withdrawal_mode}
                </CustomText>
              </View>
              <View
                style={[
                  styles.beLikeRow,
                  { justifyContent: "space-between", paddingVertical: 5 },
                ]}
              >
                <CustomText
                  style={{
                    color: "rgba(0, 0, 0, 0.56)",
                    fontFamily: "NotoSansDevanagari-Regular",
                    fontSize: 12,
                  }}
                >
                  UPI / {t(`${PATH}/accNo`)}
                </CustomText>
                <CustomText
                  style={{
                    color: "rgba(0, 0, 0, 0.56)",
                    fontFamily: "NotoSansDevanagari-Bold",
                    fontSize: 12,
                  }}
                >
                  {upi_address || account_number}
                </CustomText>
              </View>
            </>
          ) : null}
          <View style={[styles.beLikeRow, { justifyContent: "space-between" }]}>
            <View
              style={{ failure_reasonwidth: failure_reason ? "60%" : "100%" }}
            >
              <CustomText
                style={{ color: "rgba(0, 0, 0, 0.87)" }}
                variant="body_text_3"
              >
                {failure_reason || title}
              </CustomText>
              {/* <Text style={{ color: "rgba(0, 0, 0, 0.87)" }}>
                {failure_reason || title}
              </Text> */}
            </View>

            {is_blacklisted ? (
              <TouchableOpacity
                onPress={() => {
                  currentAccount().type &&
                    navigation.navigate("AddBankDetails", {
                      edit: true,
                      account_type: currentAccount().account_type,
                      data: currentAccount().account_data,
                    });
                }}
              >
                <CustomText
                  style={{ color: colors.primary }}
                  variant="body_text_3"
                >
                  {t(`${PATH}/changeAcc`)}
                </CustomText>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      </Animated.View>
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  cardContainer: {
    borderRadius: 10,
    backgroundColor: "#fff",
  },
  primaryText: {
    color: "rgba(0, 0, 0, 0.56)",
  },
});

export default WithDrawalCard;
